import { Injectable } from '@angular/core';
import { environment } from '@/environments/environment';

declare global {
  interface Window {
    pendo: any;
  }
}

@Injectable({
  providedIn: 'root'
})
export class PendoService {
  private initialized = false;
  private pendoUrl = environment.pendoUrl;

  /**
   * Initializes the Pendo analytics service with visitor and account info.
   * If Pendo is already initialized or no API key is provided, this method does nothing.
   * If the Pendo script is not yet loaded, it will load it asynchronously before initializing.
   *
   * @param visitor - Object containing visitor details such as id, email, full_name, and role
   * @param account - Object containing account details such as id, name, and type
   */
  initialize(visitor: any, account: any): void {
    if (this.initialized || !environment.isPendoEnabled) return;
    this.initialized = true;

    if (!window.pendo) {
      this.loadScript(() => this.initPendo(visitor, account));
    } else {
      this.initPendo(visitor, account);
    }
  }

  /**
   * Calls the Pendo global object's initialize method with visitor and account data.
   *
   * @param visitor - Visitor information for Pendo tracking
   * @param account - Account information for Pendo tracking
   */
  private initPendo(visitor: any, account: any): void {
    window.pendo.initialize({
      visitor,
      account,
    });
  }

  /**
   * Dynamically injects the Pendo JavaScript snippet into the document head.
   * Calls the provided callback function once the script has loaded.
   *
   * @param callback - Function to call after the Pendo script has been successfully loaded
   */
  private loadScript(callback: () => void): void {
    const script = document.createElement('script');
    script.src = this.pendoUrl;
    script.async = true;
    script.onload = callback;
    document.head.appendChild(script);
  }
}
